import { useEffect } from 'react';
import { useActiveNavItem } from '@/components/layout/use-navigation';
import { useAuthStore } from '@/features/auth/auth.store';
import { useRecordVisit, useTopPages } from './page-visits.api';

/**
 * Which pages this person actually goes to.
 *
 * Recorded through the API against the account, so the list follows them to
 * another machine. It used to live in localStorage, which meant a manager who
 * moved to a second till started from nothing and the help text on the home
 * page had to admit the list was per device.
 *
 * Ranking counts visits inside a trailing window rather than keeping a running
 * total, so old habits fade: a page opened fifty times in March should not
 * outrank the one used every day this week. The window and the threshold are
 * sent with every request, so retuning them is a change here and not a
 * deploy of the API.
 */

const WINDOW_DAYS = 30;
/** Fewer than this and it is a page they tried, not one they use. */
const MIN_VISITS = 2;
/** StrictMode runs effects twice in dev; one landing must count once. */
const DEDUPE_MS = 1000;

let lastRecorded: { key: string; at: number } | null = null;

/** Mount once inside the signed-in shell; counts each built page landed on. */
export function useRecordPageVisits() {
  const userId = useAuthStore((s) => s.profile?.id);
  const item = useActiveNavItem();
  const key = item?.status === 'built' && item.to !== '/' ? item.key : null;
  const { mutate } = useRecordVisit();

  useEffect(() => {
    if (!userId || !key) return;

    const now = Date.now();
    if (lastRecorded && lastRecorded.key === key && now - lastRecorded.at < DEDUPE_MS) {
      return;
    }
    lastRecorded = { key, at: now };

    mutate(key);
  }, [userId, key, mutate]);
}

/**
 * Nav keys ranked by visits in the window, most first.
 *
 * Empty while the request is in flight, which is what the home page wants: it
 * hides the section and shows the curated shortcuts alone rather than holding
 * a spinner over six tiles.
 */
export function useFrequentlyVisited(limit: number): string[] {
  const { data } = useTopPages({ limit, windowDays: WINDOW_DAYS, minCount: MIN_VISITS });

  return data?.map((entry) => entry.page) ?? [];
}
